import {
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Check, ChevronDown, Globe2, LoaderCircle } from 'lucide-react';
import type { WorkspaceDescriptor } from '../../types/workspace.types';
import './WorkspaceSwitcher.css';

export type WorkspaceOption = WorkspaceDescriptor;

export interface WorkspaceSwitcherProps {
    options: WorkspaceOption[];
    selectedKey?: string;
    onSelect: (option: WorkspaceOption) => void | Promise<void>;
    loading?: boolean;
    switching?: boolean;
    disabled?: boolean;
}

export const WorkspaceSwitcher = ({
    options,
    selectedKey,
    onSelect,
    loading = false,
    switching = false,
    disabled = false,
}: WorkspaceSwitcherProps) => {
    const { t } = useTranslation('common');
    const [isOpen, setIsOpen] = useState(false);

    const kindLabels: Record<WorkspaceOption['kind'], string> = {
        platform: t('workspaceSwitcher.kinds.platform'),
        institution: t('workspaceSwitcher.kinds.institution'),
    };

    const groupLabels: Record<WorkspaceOption['kind'], string> = {
        platform: t('workspaceSwitcher.groups.platform'),
        institution: t('workspaceSwitcher.groups.institution'),
    };
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const listboxId = useId();
    const selectedOption = options.find(option => option.key === selectedKey);
    const orderedOptions = useMemo(
        () => (['platform', 'institution'] as const).flatMap(kind =>
            options.filter(option => option.kind === kind),
        ),
        [options],
    );
    const isReadOnly =
        options.length === 0 ||
        (options.length === 1 && selectedOption?.key === options[0].key);
    const isUnavailable = disabled || loading || switching;

    useEffect(() => {
        if (!isOpen) return;

        const handleOutsideClick = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                triggerRef.current?.focus();
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const selectedIndex = orderedOptions.findIndex(option => option.key === selectedKey);
        optionRefs.current[Math.max(selectedIndex, 0)]?.focus();
    }, [isOpen, orderedOptions, selectedKey]);

    useEffect(() => {
        if (isUnavailable) {
            setIsOpen(false);
        }
    }, [isUnavailable]);

    const handleSelect = (option: WorkspaceOption) => {
        if (isUnavailable) return;
        void onSelect(option);
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    const handleOptionKeyDown = (
        event: ReactKeyboardEvent<HTMLButtonElement>,
        index: number,
    ) => {
        let nextIndex = index;
        if (event.key === 'ArrowDown') {
            nextIndex = (index + 1) % orderedOptions.length;
        } else if (event.key === 'ArrowUp') {
            nextIndex = (index - 1 + orderedOptions.length) % orderedOptions.length;
        } else if (event.key === 'Home') {
            nextIndex = 0;
        } else if (event.key === 'End') {
            nextIndex = orderedOptions.length - 1;
        } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setIsOpen(false);
            triggerRef.current?.focus();
            return;
        } else {
            return;
        }

        event.preventDefault();
        optionRefs.current[nextIndex]?.focus();
    };

    const renderWorkspaceIcon = (kind: WorkspaceOption['kind']) => {
        const Icon = kind === 'platform' ? Globe2 : Building2;
        return <Icon size={16} strokeWidth={1.8} aria-hidden="true" />;
    };

    const statusLabel = loading
        ? t('workspaceSwitcher.status.loading')
        : switching
            ? t('workspaceSwitcher.status.switching')
            : selectedOption?.label
                ?? (options.length === 0 ? t('workspaceSwitcher.status.noWorkspaces') : t('workspaceSwitcher.status.selectWorkspace'));

    if (isReadOnly) {
        return (
            <div className="workspace-switcher workspace-switcher--readonly" aria-label={t('workspaceSwitcher.ariaLabelCurrentWorkspace')}>
                <span className="workspace-switcher__trigger-icon">
                    {selectedOption
                        ? renderWorkspaceIcon(selectedOption.kind)
                        : <Globe2 size={15} aria-hidden="true" />}
                </span>
                <span className="workspace-switcher__readonly-value">{statusLabel}</span>
                {(loading || switching) && (
                    <LoaderCircle className="workspace-switcher__spinner" size={15} aria-hidden="true" />
                )}
            </div>
        );
    }

    return (
        <div className="workspace-switcher" ref={rootRef}>
            <button
                ref={triggerRef}
                type="button"
                className="workspace-switcher__trigger"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-label={t('workspaceSwitcher.ariaLabelWorkspaceStatus', { status: statusLabel })}
                disabled={isUnavailable}
                onClick={() => setIsOpen(open => !open)}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                        event.preventDefault();
                        setIsOpen(true);
                    }
                }}
            >
                <span className="workspace-switcher__trigger-icon">
                    {selectedOption ? renderWorkspaceIcon(selectedOption.kind) : <Globe2 size={16} aria-hidden="true" />}
                </span>
                <span className="workspace-switcher__trigger-label">{statusLabel}</span>
                {loading || switching ? (
                    <LoaderCircle className="workspace-switcher__spinner" size={16} aria-hidden="true" />
                ) : (
                    <ChevronDown
                        className={`workspace-switcher__chevron${isOpen ? ' workspace-switcher__chevron--open' : ''}`}
                        size={16}
                        aria-hidden="true"
                    />
                )}
            </button>

            {isOpen && (
                <div className="workspace-switcher__menu" id={listboxId} role="listbox" aria-label={t('workspaceSwitcher.ariaLabelAvailableWorkspaces')}>
                    {(['platform', 'institution'] as const).map(kind => {
                        const kindOptions = options.filter(option => option.kind === kind);
                        if (kindOptions.length === 0) return null;

                        return (
                            <div className="workspace-switcher__group" key={kind} role="group" aria-label={t('workspaceSwitcher.ariaLabelKindWorkspaces', { kind: kindLabels[kind] })}>
                                <div className="workspace-switcher__group-label">{groupLabels[kind]}</div>
                                {kindOptions.map(option => {
                                    const optionIndex = orderedOptions.findIndex(
                                        orderedOption => orderedOption.key === option.key,
                                    );
                                    return (
                                        <button
                                            ref={(element) => {
                                                optionRefs.current[optionIndex] = element;
                                            }}
                                            type="button"
                                            role="option"
                                            tabIndex={-1}
                                            aria-selected={selectedKey === option.key}
                                            className={`workspace-switcher__option${selectedKey === option.key ? ' workspace-switcher__option--selected' : ''}`}
                                            key={option.key}
                                            onClick={() => handleSelect(option)}
                                            onKeyDown={(event) => handleOptionKeyDown(event, optionIndex)}
                                        >
                                            <span className="workspace-switcher__option-icon">
                                                {renderWorkspaceIcon(option.kind)}
                                            </span>
                                            <span className="workspace-switcher__option-copy">
                                                <span className="workspace-switcher__option-label">{option.label}</span>
                                                <span className="workspace-switcher__option-kind">
                                                    {option.kind === 'platform'
                                                        ? t('workspaceSwitcher.kindsDescription.platform')
                                                        : t('workspaceSwitcher.kindsDescription.institution')}
                                                </span>
                                            </span>
                                            {selectedKey === option.key && (
                                                <Check className="workspace-switcher__check" size={16} aria-hidden="true" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default WorkspaceSwitcher;
